// One connectivity/sync signal for the whole Game Lite UI.
//
// Game Lite has no realtime channels (it polls), so — unlike the party game —
// the interesting signal isn't a websocket. It's these two:
//
//   'offline' the device says it has no network (navigator.onLine === false)
//   'syncing' we're online but the durable write queue still holds work that
//             hasn't reached the server (an answer in flight, or one parked
//             after repeated failures)
//   'online'  online, nothing outstanding
//
// The snapshot also carries `owed`: the writes that left the queue WITHOUT the
// server storing them (refused, expired, token wait timed out) and that the
// player has not been told about yet, split by what was lost ({ answer, rating,
// other }). An empty queue does not mean everything was saved. The queue keeps
// the tally and what has been acknowledged (writeQueue.getOwedDrops), so a loss
// is owed however late this module, or the banner, starts looking. The banner
// calls acknowledgeLoss() once it has shown one for its full hold.
//
// The player never has to wonder whether their answer made it: 'syncing' means
// "we still have it and we're still trying", and it clears the moment the
// server has acknowledged everything.

import { writeQueue } from './writeQueue';

let online = typeof navigator === 'undefined' ? true : navigator.onLine;
let pendingCount = 0;
let owed = { answer: 0, rating: 0, other: 0 };
let snapshot = { status: 'online', pending: 0, owed };
let started = false;
let detach = null; // undoes what startConnectivity attached; used by the test reset
const listeners = new Set();

function compute() {
  if (!online) return 'offline';
  if (pendingCount > 0) return 'syncing';
  return 'online';
}

function sameKinds(a, b) {
  return a.answer === b.answer && a.rating === b.rating && a.other === b.other;
}

function evaluate() {
  const status = compute();
  // useSyncExternalStore compares snapshots by identity, so only allocate a new
  // one when something actually changed — otherwise React re-renders forever.
  if (
    status === snapshot.status &&
    pendingCount === snapshot.pending &&
    sameKinds(owed, snapshot.owed)
  ) {
    return;
  }
  snapshot = { status, pending: pendingCount, owed };
  for (const cb of listeners) {
    try {
      cb();
    } catch {
      /* one bad subscriber must not break the rest */
    }
  }
}

export function startConnectivity() {
  if (started) return;
  started = true;
  const onOnline = () => {
    online = true;
    evaluate();
  };
  const onOffline = () => {
    online = false;
    evaluate();
  };
  if (typeof window !== 'undefined') {
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
  }
  const unsubscribe = writeQueue.subscribe((pending) => {
    pendingCount = pending.length;
    owed = writeQueue.getOwedDrops();
    evaluate();
  });
  detach = () => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    }
    unsubscribe();
  };
  // Whatever the queue dropped before anything was listening is owed already.
  pendingCount = writeQueue.size;
  owed = writeQueue.getOwedDrops();
  snapshot = { status: compute(), pending: pendingCount, owed };
}

export function getConnectivitySnapshot() {
  return snapshot;
}

export function subscribeConnectivity(cb) {
  startConnectivity();
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/**
 * The player has been told about this loss ({ answer, rating, other }): it is
 * not owed again. The queue publishes the change, which is how the snapshot
 * (started or not yet) comes to carry what is left.
 */
export function acknowledgeLoss(loss) {
  writeQueue.acknowledgeDrops(loss);
}

// Test-only. Detaches from the queue and the window as well, so a test can
// have the queue drop a write before connectivity is listening.
export function __resetConnectivity() {
  if (detach) detach();
  detach = null;
  online = typeof navigator === 'undefined' ? true : navigator.onLine;
  pendingCount = 0;
  owed = { answer: 0, rating: 0, other: 0 };
  snapshot = { status: 'online', pending: 0, owed };
  started = false;
  listeners.clear();
}
